import { useState } from "react";
import { api, gbp, API } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Tag, CheckCircle2, FileText } from "lucide-react";

export const QuoteDialog = ({ flight, trigger }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passengers, setPassengers] = useState([{ first: "", last: "", dob: "" }]);
  const [contact, setContact] = useState("");
  const [email, setEmail] = useState("");
  const [voucher, setVoucher] = useState("");
  const [percent, setPercent] = useState(0);
  const [myPrice, setMyPrice] = useState("");
  const [done, setDone] = useState(null);

  const setP = (i, k, v) => setPassengers((ps) => ps.map((p, idx) => idx === i ? { ...p, [k]: v } : p));
  const addP = () => setPassengers((ps) => [...ps, { first: "", last: "", dob: "" }]);
  const delP = (i) => setPassengers((ps) => ps.filter((_, idx) => idx !== i));

  const applyVoucher = async () => {
    if (!voucher) return;
    try {
      const { data } = await api.get(`/voucher/${voucher.trim()}`);
      if (data.valid) { setPercent(data.percent); toast.success(`Voucher applied — ${data.percent}% off!`); }
      else { setPercent(0); toast.error(data.message); }
    } catch { toast.error("Could not check voucher"); }
  };

  const pax = passengers.length;
  const perPerson = Number(myPrice) > 0 ? Number(myPrice) : flight.price;
  const subtotal = perPerson * pax;
  const total = subtotal * (1 - percent / 100);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/quotes", {
        flights: [flight], passengers, contact_number: contact, email, voucher: percent ? voucher : "",
        ...(Number(myPrice) > 0 ? { custom_price: Number(myPrice) } : {}),
      });
      setDone(data);
      if (data.emailed) toast.success(`Quote ${data.reference} emailed to ${email}`);
      else toast.success(`Quote ${data.reference} created`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create quote");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setDone(null); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><FileText className="w-5 h-5 text-[#FF6B35]" /> Email flight quote</DialogTitle>
          <DialogDescription>{flight.airline} · {flight.origin_code} → {flight.destination_code} · {flight.cabin}</DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="text-center py-6" data-testid="quote-success">
            <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto" />
            <h3 className="font-display text-xl font-bold text-slate-900 mt-3">Quote {done.reference} ready</h3>
            <p className="text-slate-500 text-sm mt-2">{done.emailed ? `We've emailed your PDF quote to ${email}.` : "Download your PDF quote below or add an email to have it sent."}</p>
            <p className="font-display text-2xl font-bold text-[#0B3C5D] mt-3">{gbp(done.total)}</p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center mt-4">
              <a data-testid="quote-download-pdf" href={`${API}/quotes/${done.id}/pdf`} target="_blank" rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FF6B35] hover:bg-[#e85a26] text-white font-semibold px-6 py-2.5 text-sm">
                <FileText className="w-4 h-4" /> Download quote PDF
              </a>
              <a data-testid="quote-download-itinerary" href={`${API}/quotes/${done.id}/itinerary-pdf`} target="_blank" rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 text-[#0B3C5D] font-semibold px-6 py-2.5 text-sm">
                <FileText className="w-4 h-4" /> Flight details PDF
              </a>
            </div>
            <Button onClick={() => setOpen(false)} className="mt-4 rounded-full bg-[#0B3C5D] text-white px-8">Done</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-3">
              {passengers.map((p, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end">
                  <div><Label className="text-xs">First name</Label><Input data-testid={`q-first-${i}`} required value={p.first} onChange={(e) => setP(i, "first", e.target.value)} /></div>
                  <div><Label className="text-xs">Last name</Label><Input data-testid={`q-last-${i}`} required value={p.last} onChange={(e) => setP(i, "last", e.target.value)} /></div>
                  <div><Label className="text-xs">DOB (opt)</Label><Input data-testid={`q-dob-${i}`} type="date" value={p.dob} onChange={(e) => setP(i, "dob", e.target.value)} /></div>
                  {passengers.length > 1 ? <button type="button" onClick={() => delP(i)} className="text-slate-400 hover:text-red-500 pb-2"><Trash2 className="w-4 h-4" /></button> : <span />}
                </div>
              ))}
              <button type="button" onClick={addP} data-testid="q-add-pax" className="text-sm text-[#0B3C5D] font-semibold flex items-center gap-1"><Plus className="w-4 h-4" /> Add passenger</button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Contact number</Label><Input data-testid="q-contact" required value={contact} onChange={(e) => setContact(e.target.value)} /></div>
              <div><Label>Email (optional)</Label><Input data-testid="q-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="to receive PDF" /></div>
            </div>
            <div>
              <Label className="flex items-center gap-1"><Tag className="w-3.5 h-3.5" /> Your price per passenger (optional)</Label>
              <Input data-testid="q-my-price" type="number" min="0" step="0.01" value={myPrice} onChange={(e) => setMyPrice(e.target.value)} placeholder={`Live fare ${gbp(flight.price)} — leave blank to use it`} />
              <p className="text-[11px] text-slate-400 mt-1">Set your own selling price and the client's quote shows exactly this.</p>
            </div>
            <div>
              <Label className="flex items-center gap-1"><Tag className="w-3.5 h-3.5" /> Voucher code</Label>
              <div className="flex gap-2">
                <Input data-testid="q-voucher" value={voucher} onChange={(e) => setVoucher(e.target.value)} placeholder="e.g. ELIA202610" />
                <Button type="button" data-testid="q-apply-voucher" onClick={applyVoucher} variant="outline" className="rounded-md shrink-0">Apply</Button>
              </div>
            </div>
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 space-y-1 text-sm">
              <div className="flex justify-between text-slate-600"><span>{pax} passenger(s) × {gbp(perPerson)}</span><span>{gbp(subtotal)}</span></div>
              {percent > 0 && <div className="flex justify-between text-[#FF6B35] font-medium"><span>Voucher −{percent}%</span><span>−{gbp(subtotal * percent / 100)}</span></div>}
              <div className="flex justify-between font-display text-lg font-bold text-[#0B3C5D] pt-1 border-t border-slate-200"><span>Total</span><span data-testid="q-total">{gbp(total)}</span></div>
            </div>
            <Button data-testid="q-submit" type="submit" disabled={loading} className="w-full rounded-full bg-[#FF6B35] hover:bg-[#e85a26] text-white font-semibold py-6">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Get my quote"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
